import Button from '../components/Button';
import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { HeroBackgroundPaths } from '../components/ui/background-paths';

const FACEBOOK_ICON_PATH = 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z';

export default function HeroSection() {
  const { t, isRTL } = useI18n();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const socialLinks = [
    { label: t.hero.social.group, url: 'https://www.facebook.com/share/g/1C5xbs8Jic/' },
    { label: t.hero.social.ceo, url: 'https://www.facebook.com/ahmed.n.khedry' },
  ];

  return (
    <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-white pt-24 pb-12">
      <HeroBackgroundPaths />
      <div className="absolute inset-0 bg-slate-900/[0.03] z-0 pointer-events-none"></div>

      <div className="container mx-auto px-4 sm:px-8 md:px-12 lg:px-16 relative z-10 mt-8">
        <div className="max-w-4xl mx-auto flex flex-col items-center text-center space-y-6 md:space-y-8">
          {/* Main Headline */}
          <div className="space-y-4">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.15] tracking-tight">
              <span className="text-[#2C2C2C]">{t.hero.title.line1}</span>
              <br />
              <span className="relative inline-block mt-2">
                <span className="relative z-10 text-[#5B3FD0]">{t.hero.title.line2}</span>
                {/* Underline */}
                <div className="absolute bottom-1 md:bottom-2 left-0 right-0 h-2 md:h-3 bg-[#9FFFCB]/40 -z-0"></div>
              </span>
              <br />
              <span className="text-[#2C2C2C] mt-2 inline-block">{t.hero.title.line3}</span>
            </h1>
          </div>

          {/* Description */}
          <p className="text-base md:text-lg text-[#606060] leading-relaxed max-w-2xl mx-auto">
            {t.hero.description}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row flex-wrap justify-center items-center gap-4 pt-2 md:pt-4 w-full sm:w-auto">
            <Button 
              variant="primary" 
              className="text-sm md:text-base px-8 py-3.5 shadow-xl hover:shadow-2xl transition-all duration-300 w-full sm:w-auto flex items-center justify-center gap-2"
              onClick={() => navigate(isAuthenticated ? '/dashboard' : '/login')}
            >
              <span className="font-semibold">{t.hero.cta.primary}</span>
              <svg className={`w-4 h-4 md:w-5 md:h-5 ${isRTL ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Button>

            {socialLinks.map((link) => (
              <a
                key={link.url}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-white border border-gray-200 text-sm md:text-base font-semibold text-[#2C2C2C] shadow-sm hover:border-[#1877F2] hover:text-[#1877F2] hover:shadow-md transition-all duration-300"
              >
                <svg className="w-5 h-5 text-[#1877F2]" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d={FACEBOOK_ICON_PATH} />
                </svg>
                <span>{link.label}</span>
              </a>
            ))}
          </div>

          {/* Features List */}
          <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-6 md:gap-10 pt-4 md:pt-8 border-t border-gray-100 w-full">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-[#9FFFCB]/20 flex items-center justify-center flex-shrink-0">
                <div className="w-6 h-6 md:w-7 md:h-7 rounded-full bg-[#9FFFCB] flex items-center justify-center">
                  <svg className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#2C2C2C]" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <span className="text-sm md:text-base text-[#2C2C2C] font-semibold">{t.hero.features.smartMatching}</span>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-[#9FFFCB]/20 flex items-center justify-center flex-shrink-0">
                <div className="w-6 h-6 md:w-7 md:h-7 rounded-full bg-[#9FFFCB] flex items-center justify-center">
                  <svg className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#2C2C2C]" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <span className="text-sm md:text-base text-[#2C2C2C] font-semibold">{t.hero.features.verifiedConsultants}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
